import { useState } from 'react';
import { trackEvent } from '../lib/tracking';
import { WHATSAPP_NUMBER } from '@/config/site';

const situacoes = [
  'Nova sede',
  'Mudança de endereço',
  'Reforma',
  'Expansão',
  'Projeto de arquiteto',
  'Implantação de novo setor',
  'Outro',
];

const necessidades = [
  'Mobiliário',
  'Divisórias',
  'Carpete',
  'Acústica',
  'Marcenaria',
  'Implantação completa',
];

const FAIXAS_INVESTIMENTO = [
  'Até R$ 20 mil',
  'R$ 20 mil a R$ 50 mil',
  'R$ 50 mil a R$ 100 mil',
  'R$ 100 mil a R$ 250 mil',
  'R$ 250 mil a R$ 500 mil',
  'Acima de R$ 500 mil',
  'Ainda estamos definindo',
];

const FAIXAS_QUALIFICADAS = [
  'R$ 50 mil a R$ 100 mil',
  'R$ 100 mil a R$ 250 mil',
  'R$ 250 mil a R$ 500 mil',
  'Acima de R$ 500 mil',
];

const prazos = [
  'Imediatamente',
  'Até 30 dias',
  '30 a 60 dias',
  '60 a 90 dias',
  '3 a 6 meses',
  'Ainda planejando',
];

const plantaOpcoes = ['Sim', 'Não', 'Arquiteto está desenvolvendo'];

export default function QualificacaoForm() {
  const [nome, setNome] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [empresa, setEmpresa] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [cidade, setCidade] = useState('');
  const [situacao, setSituacao] = useState('');
  const [selecionados, setSelecionados] = useState<string[]>([]);
  const [investimento, setInvestimento] = useState('');
  const [prazo, setPrazo] = useState('');
  const [planta, setPlanta] = useState('');
  const [erro, setErro] = useState('');
  const [started, setStarted] = useState(false);

  const marcarStart = () => {
    if (!started) {
      setStarted(true);
      trackEvent('form_start', { source: 'formulario_projetos' });
    }
  };

  const toggleNecessidade = (item: string) => {
    marcarStart();
    setErro('');
    setSelecionados((prev) =>
      prev.includes(item) ? prev.filter((n) => n !== item) : [...prev, item]
    );
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!nome.trim() || !whatsapp.trim() || !empresa.trim() || !cidade.trim()) {
      setErro('Por favor, preencha seu nome, WhatsApp, empresa e cidade.');
      return;
    }
    if (!situacao) {
      setErro('Selecione a situação atual da sua empresa.');
      return;
    }
    if (selecionados.length === 0) {
      setErro('Selecione pelo menos uma solução necessária.');
      return;
    }
    if (!investimento) {
      setErro('Selecione a faixa estimada de investimento do projeto.');
      return;
    }

    setErro('');

    // Evento de lead qualificado 50k+
    const isQualificado = FAIXAS_QUALIFICADAS.includes(investimento);
    trackEvent('form_submit_projetos', {
      situacao,
      necessidades: selecionados,
      investimento,
      prazo: prazo || 'nao_informado',
    });

    if (isQualificado) {
      trackEvent('qualified_lead_50k_plus', {
        service: selecionados.join(', '),
        city: cidade.trim(),
        investment_range: investimento,
        project_stage: situacao,
        deadline: prazo || 'nao_informado',
      });
    }

    const linhas = [
      'Olá! Vim pelo site da Home Office Premium e quero solicitar uma análise do meu projeto corporativo.',
      '',
      `Nome: ${nome.trim()}`,
      `WhatsApp: ${whatsapp.trim()}`,
      `Empresa: ${empresa.trim()}`,
      cnpj.trim() ? `CNPJ: ${cnpj.trim()}` : null,
      `Cidade/UF: ${cidade.trim()}`,
      '',
      `Situação atual: ${situacao}`,
      `Soluções necessárias: ${selecionados.join(', ')}`,
      `Faixa de investimento: ${investimento}`,
      prazo ? `Quando implantar: ${prazo}` : null,
      planta ? `Possui planta/projeto: ${planta}` : null,
    ].filter(Boolean) as string[];

    const mensagem = encodeURIComponent(linhas.join('\n'));
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${mensagem}`, '_blank', 'noopener,noreferrer');
  };

  const inputClass =
    'w-full bg-white border border-background-300/70 rounded-xl px-4 py-3.5 text-[15px] text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors';

  const labelClass = 'block text-foreground-800 text-[13px] font-semibold mb-2';

  const pillAtivo = 'bg-primary-500 text-white border-primary-500';
  const pillInativo =
    'bg-white text-foreground-700 border-background-300/70 hover:border-primary-300';

  return (
    <form onSubmit={handleSubmit} className="w-full" noValidate>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="pc-nome" className={labelClass}>
            Nome <span className="text-primary-500">*</span>
          </label>
          <input
            id="pc-nome"
            name="nome"
            type="text"
            autoComplete="name"
            value={nome}
            onChange={(e) => { marcarStart(); setNome(e.target.value); }}
            onFocus={marcarStart}
            placeholder="Seu nome"
            className={inputClass}
          />
        </div>

        <div>
          <label htmlFor="pc-whatsapp" className={labelClass}>
            WhatsApp <span className="text-primary-500">*</span>
          </label>
          <input
            id="pc-whatsapp"
            name="whatsapp"
            type="tel"
            autoComplete="tel"
            value={whatsapp}
            onChange={(e) => { marcarStart(); setWhatsapp(e.target.value); }}
            onFocus={marcarStart}
            placeholder="(81) 90000-0000"
            className={inputClass}
          />
        </div>

        <div>
          <label htmlFor="pc-empresa" className={labelClass}>
            Empresa <span className="text-primary-500">*</span>
          </label>
          <input
            id="pc-empresa"
            name="empresa"
            type="text"
            autoComplete="organization"
            value={empresa}
            onChange={(e) => { marcarStart(); setEmpresa(e.target.value); }}
            onFocus={marcarStart}
            placeholder="Nome da empresa"
            className={inputClass}
          />
        </div>

        <div>
          <label htmlFor="pc-cnpj" className={labelClass}>
            CNPJ <span className="text-foreground-400 font-normal">(opcional)</span>
          </label>
          <input
            id="pc-cnpj"
            name="cnpj"
            type="text"
            value={cnpj}
            onChange={(e) => { marcarStart(); setCnpj(e.target.value); }}
            onFocus={marcarStart}
            placeholder="00.000.000/0001-00"
            className={inputClass}
          />
        </div>

        <div className="sm:col-span-2">
          <label htmlFor="pc-cidade" className={labelClass}>
            Cidade / UF <span className="text-primary-500">*</span>
          </label>
          <input
            id="pc-cidade"
            name="cidade"
            type="text"
            autoComplete="address-level2"
            value={cidade}
            onChange={(e) => { marcarStart(); setCidade(e.target.value); }}
            onFocus={marcarStart}
            placeholder="Ex: Recife / PE"
            className={inputClass}
          />
        </div>
      </div>

      {/* Situação */}
      <div className="mt-7">
        <span className={labelClass}>
          Qual é a situação atual? <span className="text-primary-500">*</span>
        </span>
        <div className="flex flex-wrap gap-2.5">
          {situacoes.map((s) => {
            const ativo = situacao === s;
            return (
              <button
                key={s}
                type="button"
                onClick={() => { marcarStart(); setErro(''); setSituacao(s); }}
                className={`px-4 py-2.5 rounded-full text-[13px] font-medium border transition-colors whitespace-nowrap cursor-pointer ${ativo ? pillAtivo : pillInativo}`}
              >
                {s}
              </button>
            );
          })}
        </div>
      </div>

      {/* Soluções (multi) */}
      <div className="mt-7">
        <span className={labelClass}>
          Quais soluções precisa? <span className="text-primary-500">*</span>
        </span>
        <div className="flex flex-wrap gap-2.5">
          {necessidades.map((n) => {
            const ativo = selecionados.includes(n);
            return (
              <button
                key={n}
                type="button"
                onClick={() => toggleNecessidade(n)}
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-[13px] font-medium border transition-colors whitespace-nowrap cursor-pointer ${ativo ? pillAtivo : pillInativo}`}
              >
                {ativo && <i className="ri-check-line text-sm"></i>}
                {n}
              </button>
            );
          })}
        </div>
      </div>

      {/* Faixa de investimento */}
      <div className="mt-7">
        <span className={labelClass}>
          Qual a faixa estimada de investimento do projeto? <span className="text-primary-500">*</span>
        </span>
        <div className="flex flex-wrap gap-2.5">
          {FAIXAS_INVESTIMENTO.map((f) => {
            const ativo = investimento === f;
            const isQualificada = FAIXAS_QUALIFICADAS.includes(f);
            return (
              <button
                key={f}
                type="button"
                onClick={() => { marcarStart(); setErro(''); setInvestimento(f); }}
                className={`px-4 py-2.5 rounded-full text-[13px] font-medium border transition-colors whitespace-nowrap cursor-pointer ${
                  ativo
                    ? 'bg-primary-500 text-white border-primary-500'
                    : isQualificada
                    ? 'bg-white text-foreground-700 border-primary-200 hover:border-primary-400'
                    : 'bg-white text-foreground-700 border-background-300/70 hover:border-primary-300'
                }`}
              >
                {f}
              </button>
            );
          })}
        </div>
      </div>

      {/* Prazo */}
      <div className="mt-7">
        <span className={labelClass}>Quando pretende implantar? <span className="text-foreground-400 font-normal">(opcional)</span></span>
        <div className="flex flex-wrap gap-2.5">
          {prazos.map((p) => {
            const ativo = prazo === p;
            return (
              <button
                key={p}
                type="button"
                onClick={() => { marcarStart(); setPrazo(p); }}
                className={`px-4 py-2.5 rounded-full text-[13px] font-medium border transition-colors whitespace-nowrap cursor-pointer ${ativo ? pillAtivo : pillInativo}`}
              >
                {p}
              </button>
            );
          })}
        </div>
      </div>

      {/* Planta */}
      <div className="mt-7">
        <span className={labelClass}>Já possui planta ou projeto? <span className="text-foreground-400 font-normal">(opcional)</span></span>
        <div className="flex flex-wrap gap-2.5">
          {plantaOpcoes.map((p) => {
            const ativo = planta === p;
            return (
              <button
                key={p}
                type="button"
                onClick={() => { marcarStart(); setPlanta(p); }}
                className={`px-4 py-2.5 rounded-full text-[13px] font-medium border transition-colors whitespace-nowrap cursor-pointer ${ativo ? pillAtivo : pillInativo}`}
              >
                {p}
              </button>
            );
          })}
        </div>
      </div>

      {erro && (
        <p className="mt-6 text-[14px] text-primary-600 bg-primary-50 border border-primary-200 rounded-xl px-4 py-3">
          {erro}
        </p>
      )}

      <button
        type="submit"
        className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 text-white text-[15px] font-semibold px-7 py-4 rounded-full transition-colors whitespace-nowrap cursor-pointer"
      >
        Solicitar análise do projeto
        <i className="ri-whatsapp-line text-lg"></i>
      </button>

      <p className="mt-4 text-center text-foreground-400 text-[12px] leading-relaxed">
        Ao enviar, você será direcionado ao WhatsApp com suas respostas já preenchidas.
      </p>
    </form>
  );
}